import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import type { ReactNode } from 'react'
import { CollapsiblePrompt } from 'v2/shared/components/collapsible-prompt'
import { getNodeOutcome } from '../node-outcome'
import type { NodeExecutedEvent } from '../types'

interface NodeExecutionDetailsProps {
  node: NodeExecutedEvent
}

const formatDuration = (ms: number): string =>
  ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(2)}s`

export const NodeExecutionDetails = ({ node }: NodeExecutionDetailsProps) => {
  const cfg = node.node_config

  const rows = new Map<string, ReactNode>()

  // HTTP response code — red when it failed
  if (cfg?.status_code != null) {
    const code = Number(cfg.status_code)
    const isError = !Number.isNaN(code) && code >= 400
    rows.set(
      'Response code',
      <span className={`font-jetbrains tabular-nums ${isError ? 'text-destructive' : ''}`}>
        {cfg.status_code}
      </span>,
    )
  }

  // HTTP response body
  if (cfg?.response) {
    rows.set('Response', <CollapsiblePrompt maxWords={20} text={cfg.response} />)
  }

  // Past runs of the retired Skills node have no dedicated view, so their input and result show here
  if (cfg?.skill_input) {
    rows.set('Input', <CollapsiblePrompt maxWords={20} text={JSON.stringify(cfg.skill_input)} />)
  }
  if (cfg?.skill_result) {
    const result =
      typeof cfg.skill_result === 'string' ? cfg.skill_result : JSON.stringify(cfg.skill_result)
    rows.set('Result', <CollapsiblePrompt maxWords={20} text={result} />)
  }

  // Time taken — from the backend node duration only (execution_time in ms, or
  // tool/HTTP seconds). Not shown when the backend doesn't provide it.
  const executionTime = cfg?.execution_time != null ? Number(cfg.execution_time) : null
  const seconds = cfg?.time_taken ?? cfg?.tool_time_taken
  const secondsNum = seconds != null ? Number(seconds) : null
  let timeTaken: string | null = null
  if (executionTime != null && !Number.isNaN(executionTime)) {
    timeTaken = formatDuration(executionTime)
  } else if (secondsNum != null && !Number.isNaN(secondsNum)) {
    timeTaken = `${secondsNum.toFixed(2)}s`
  }
  if (timeTaken) {
    rows.set('Time taken', <span className="font-jetbrains tabular-nums">{timeTaken}</span>)
  }

  if (rows.size === 0) return null

  // Auto-open for failed/warning nodes, collapsed for successful ones.
  const isOpen = getNodeOutcome(node) !== 'success'

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={isOpen ? 'execution-details' : undefined}
      className="mt-3 w-full border-t border-border"
    >
      <AccordionItem value="execution-details" className="border-none">
        <AccordionTrigger
          className="py-3 font-inter text-s-500 text-secondary no-underline hover:text-foreground hover:no-underline [&>svg]:ml-auto"
          data-testid="accordion-execution-details"
        >
          <div className="no-underline text-s-500">Execution details</div>
        </AccordionTrigger>
        <AccordionContent className="pb-2">
          <div className="flex flex-col gap-2">
            {Array.from(rows.entries()).map(([key, value]) => (
              <div key={key} className="flex items-start gap-4">
                <span className="text-xs-400 text-secondary w-28 shrink-0">{key}:</span>
                <span className="text-s-400 text-primary min-w-0 break-words [overflow-wrap:anywhere]">
                  {value}
                </span>
              </div>
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
